/*
 * 检查项管理(明阳智能)入口访问控制
 *
 * 数据来源:远程 KV 配置 workspace_inspection_config(通过 /web/config/query
 * 拉取,存入 Redux).**完全由后端 KV 接管**,前端不留 hardcode 白名单 —
 * 后端关掉就所有团队都不显示,后端没下发就同样不显示.
 *
 * KV value 结构(JSON 字符串):
 *   {
 *     "enable": true,                       // 总开关:false 则所有团队都不显示入口
 *     "groupIds": ["1234567", "8901234"]    // 灰度白名单(invite_id 或 groupId 均接受)
 *                                           // 空数组 / 缺省时 → 全量开放(只看 enable)
 *   }
 *
 * 用法对齐 todayClaw/clawAccessConfig.ts.鸿蒙仓库对应文件:
 *   XCamera_Harmony_RN/.../XCWorkBenchConfigData.tsx shouldShowInspectionItem.
 */

export interface InspectionAccessConfig {
  enable?: boolean
  groupIds?: string[]
}

export const INSPECTION_CONFIG_KEY = 'workspace_inspection_config'

/**
 * 解析远程 KV 配置的 JSON 字符串.格式错 / 字段缺 / parse 失败 → 返回 null,
 * 上层视为"未开启",入口隐藏.
 */
export function parseInspectionConfig(
  rawValue: string | undefined | null
): InspectionAccessConfig | null {
  if (!rawValue) return null
  try {
    const parsed = JSON.parse(rawValue)
    if (typeof parsed?.enable !== 'boolean') {
      // eslint-disable-next-line no-console
      console.warn('[InspectionConfig] 远程配置缺 enable 字段:', parsed)
      return null
    }
    return parsed as InspectionAccessConfig
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[InspectionConfig] 远程配置 JSON 解析失败:', e)
    return null
  }
}

/**
 * 判断当前团队是否能看到「检查项管理」入口.
 *
 * @param rawConfigValue - state.main.config?.workspace_inspection_config
 * @param inviteId        - 当前团队短 ID (state.group.invite_id)
 * @param groupId         - 当前团队 UUID  (state.group.groupId)
 *
 * 决策树(完全由远程 KV 决定):
 *   - KV 没拉到 / parse 失败 → 隐藏
 *   - enable=false → 隐藏
 *   - enable=true + groupIds 为空/缺 → 全量开放(所有团队可见)
 *   - enable=true + groupIds 非空    → invite_id / groupId 命中白名单才可见
 */
export function shouldShowInspectionEntry(
  rawConfigValue: string | undefined | null,
  inviteId: string | undefined | null,
  groupId: string | undefined | null
): boolean {
  const config = parseInspectionConfig(rawConfigValue)
  if (!config || !config.enable) return false
  if (!config.groupIds || config.groupIds.length === 0) return true

  const candidates = [inviteId, groupId].filter(Boolean).map((id) => String(id))
  if (candidates.length === 0) return false

  const whitelist = config.groupIds.map((id) => String(id))
  return candidates.some((id) => whitelist.includes(id))
}
